import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface CuisineOption {
  id: string;
  name: string;
  icon: string;
  description: string;
  popular?: boolean;
}

interface CuisineGridProps {
  selectedCuisines: string[];
  onSelectionChange: (cuisines: string[]) => void;
  maxSelections?: number;
  className?: string;
}

const CUISINE_OPTIONS: CuisineOption[] = [
  { id: 'italian', name: 'Italian', icon: '🍝', description: 'Pasta, pizza, risotto', popular: true },
  { id: 'mexican', name: 'Mexican', icon: '🌮', description: 'Tacos, burritos, salsas', popular: true },
  { id: 'asian', name: 'Asian', icon: '🥡', description: 'Stir-fries, noodles, rice dishes', popular: true },
  { id: 'american', name: 'American', icon: '🍔', description: 'Burgers, BBQ, comfort food', popular: true },
  { id: 'mediterranean', name: 'Mediterranean', icon: '🫒', description: 'Greek, healthy, olive oil', popular: true },
  { id: 'indian', name: 'Indian', icon: '🍛', description: 'Curries, spices, rice', popular: true },
  { id: 'french', name: 'French', icon: '🥖', description: 'Classic techniques, wine', popular: false },
  { id: 'thai', name: 'Thai', icon: '🌶️', description: 'Spicy, herbs, coconut', popular: false },
  { id: 'japanese', name: 'Japanese', icon: '🍣', description: 'Sushi, miso, clean flavors', popular: false },
  { id: 'middle-eastern', name: 'Middle Eastern', icon: '🧆', description: 'Hummus, falafel, spices', popular: false },
  { id: 'german', name: 'German', icon: '🥨', description: 'Sausages, beer, hearty', popular: false },
  { id: 'korean', name: 'Korean', icon: '🥢', description: 'Kimchi, BBQ, fermented', popular: false },
];

export function CuisineGrid({ 
  selectedCuisines, 
  onSelectionChange, 
  maxSelections = 5,
  className = '' 
}: CuisineGridProps) {
  const handleCuisineToggle = (cuisineId: string) => {
    const isSelected = selectedCuisines.includes(cuisineId);
    
    if (isSelected) {
      // Remove cuisine
      onSelectionChange(selectedCuisines.filter(id => id !== cuisineId));
    } else {
      // Add cuisine (if under limit)
      if (selectedCuisines.length < maxSelections) {
        onSelectionChange([...selectedCuisines, cuisineId]);
      }
    }
  };

  const popularCuisines = CUISINE_OPTIONS.filter(c => c.popular);
  const otherCuisines = CUISINE_OPTIONS.filter(c => !c.popular);

  const renderCuisineCard = (cuisine: CuisineOption) => {
    const isSelected = selectedCuisines.includes(cuisine.id);
    const isAtLimit = selectedCuisines.length >= maxSelections && !isSelected;

    return (
      <button
        key={cuisine.id}
        onClick={() => handleCuisineToggle(cuisine.id)}
        disabled={isAtLimit}
        className={cn(
          'relative p-4 rounded-lg border-2 transition-all duration-200 text-left group',
          'desktop-hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-2',
          isSelected
            ? 'border-coral bg-coral/5 shadow-md'
            : 'border-gray-200 bg-white desktop-hover:border-gray-300 desktop-hover:shadow-sm',
          isAtLimit && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-2 right-2 w-6 h-6 bg-coral text-white rounded-full flex items-center justify-center">
            <Check className="w-4 h-4" />
          </div>
        )}

        {/* Cuisine content */}
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{cuisine.icon}</span>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 font-header truncate">
                {cuisine.name}
              </h3>
              {cuisine.popular && (
                <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                  Popular
                </span>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            {cuisine.description}
          </p>
        </div>

        {/* Hover effect */}
        <div className={cn(
          'absolute inset-0 rounded-lg bg-coral/5 opacity-0 transition-opacity duration-200',
          'group-desktop-desktop-hover:opacity-100',
          isSelected && 'opacity-100'
        )} />
      </button>
    );
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Selection counter */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900 font-header">
          Choose your favorite cuisines
        </h3>
        <span className="text-sm text-gray-600">
          {selectedCuisines.length}/{maxSelections} selected
        </span>
      </div>

      {/* Scrollable cuisine content */}
      <div className="max-h-[60vh] overflow-y-auto space-y-6 pr-2">
        {/* Popular cuisines */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Most Popular</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {popularCuisines.map(renderCuisineCard)}
          </div>
        </div>

        {/* Other cuisines */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">More Options</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {otherCuisines.map(renderCuisineCard)}
          </div>
        </div>
      </div>

      {/* Help text */}
      <p className="text-xs text-gray-500 leading-relaxed">
        Select up to {maxSelections} cuisines that you enjoy most. This helps us recommend recipes you'll love.
      </p>
    </div>
  );
}